import { TrackingEventName } from '@/models/analytics/eventTypes';
import {
  type NoteAutoRetryDecidedEventProps,
  type NoteEditStartedEventProps,
} from '@/models/analytics/features/notes';
import { trackSelfDescribingEvent } from '@snowplow/browser-tracker';
import {
  trackNoteContentPastedSpec,
  trackNoteCopiedSpec,
  trackNoteCreatedSpec,
  trackNoteDisplayedSpec,
  trackNoteEditorRenderedSpec,
  trackNoteGenerationCompletedSpec,
  trackNoteGenerationStartedSpec,
  trackNoteInsertAppliedSpec,
  trackNotePushFailedSpec,
  trackNoteRevertedSpec,
  trackNoteSelectedSpec,
  trackNoteSelectionSkippedSpec,
  trackNoteStartedSpec,
  trackNoteSyncedSpec,
  trackNoteSyncFailedSpec,
  trackNoteUpdatedSpec,
  trackSectionCreatedSpec,
  trackSectionDeletedSpec,
  type NoteContentPasted,
  type NoteCopied,
  type NoteCreated,
  type NoteDisplayed,
  type NoteEditorRendered,
  type NoteGenerationCompleted,
  type NoteGenerationStarted,
  type NoteInsertApplied,
  type NotePushFailed,
  type NoteReverted,
  type NoteSelected,
  type NoteSelectionSkipped,
  type NoteStarted,
  type NoteSynced,
  type NoteSyncFailed,
  type NoteUpdated,
  type SectionCreated,
  type SectionDeleted,
} from '@snowtype/snowplow';
import type { SnowplowEventMap } from '../trackSnowplowEvent';

export const noteEvents: SnowplowEventMap = {
  [TrackingEventName.NoteCreated]: (props) => trackNoteCreatedSpec(props as NoteCreated),
  [TrackingEventName.NoteSelected]: (props) =>
    trackNoteSelectedSpec(props as NoteSelected),
  [TrackingEventName.NoteDisplayed]: (props) =>
    trackNoteDisplayedSpec(props as NoteDisplayed),
  [TrackingEventName.NoteSelectionSkipped]: (props) =>
    trackNoteSelectionSkippedSpec(props as NoteSelectionSkipped),
  [TrackingEventName.NoteUpdated]: (props) => trackNoteUpdatedSpec(props as NoteUpdated),
  [TrackingEventName.NoteReverted]: (props) =>
    trackNoteRevertedSpec(props as NoteReverted),
  [TrackingEventName.NoteCopied]: (props) => trackNoteCopiedSpec(props as NoteCopied),
  [TrackingEventName.NoteContentPasted]: (props) =>
    trackNoteContentPastedSpec(props as NoteContentPasted),
  [TrackingEventName.NoteStarted]: (props) => trackNoteStartedSpec(props as NoteStarted),
  [TrackingEventName.NoteGenerationStarted]: (props) =>
    trackNoteGenerationStartedSpec(props as NoteGenerationStarted),
  [TrackingEventName.NoteGenerationCompleted]: (props) =>
    trackNoteGenerationCompletedSpec(props as NoteGenerationCompleted),
  [TrackingEventName.SectionCreated]: (props) =>
    trackSectionCreatedSpec(props as SectionCreated),
  [TrackingEventName.SectionDeleted]: (props) =>
    trackSectionDeletedSpec(props as SectionDeleted),
  // TODO(data): wire up `[TrackingEventName.NoteFailed]` once the
  // `iglu:com.heidi/note_failed` schema is registered. Until then the event
  // still flows to Amplitude + Datadog via `trackEventV2`, and the Snowplow
  // tracker silently no-ops for unregistered handlers.
  [TrackingEventName.NoteEditorRendered]: (props) =>
    trackNoteEditorRenderedSpec(props as NoteEditorRendered),
  // TODO(snowtype): swap to the generated Spec fn once Snowtype is regenerated
  // (needs console credentials). The URI must match the published schema.
  [TrackingEventName.NoteEditStarted]: (props) =>
    trackSelfDescribingEvent({
      event: {
        schema: 'iglu:com.heidi/note_edit_started/jsonschema/1-0-1',
        data: props as NoteEditStartedEventProps,
      },
    }),
  // TODO(snowtype): swap to the generated Spec fn once Snowtype is regenerated
  // (needs console credentials). The URI must match the published schema.
  [TrackingEventName.NoteAutoRetryDecided]: (props) =>
    trackSelfDescribingEvent({
      event: {
        schema: 'iglu:com.heidi/note_auto_retry_decided/jsonschema/1-0-0',
        data: props as NoteAutoRetryDecidedEventProps,
      },
    }),
  [TrackingEventName.NoteInsertApplied]: (props) =>
    trackNoteInsertAppliedSpec(props as NoteInsertApplied),
  [TrackingEventName.NotePushFailed]: (props) =>
    trackNotePushFailedSpec(props as NotePushFailed),
  [TrackingEventName.NoteSyncFailed]: (props) =>
    trackNoteSyncFailedSpec(props as NoteSyncFailed),
  [TrackingEventName.NoteSynced]: (props) => trackNoteSyncedSpec(props as NoteSynced),
};
